import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, parseISO } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return "—";
  const d = typeof date === "string" ? parseISO(date) : date;
  return format(d, "MMM d, yyyy");
}

export function formatNumber(num: number | null | undefined): string {
  if (num === null || num === undefined) return "—";
  return new Intl.NumberFormat().format(num);
}

export function formatPercent(num: number | null | undefined, decimals = 1): string {
  if (num === null || num === undefined) return "—";
  return `${num.toFixed(decimals)}%`;
}

export function truncate(str: string | null | undefined, length: number): string {
  if (!str) return "";
  if (str.length <= length) return str;
  return str.slice(0, length) + "...";
}

export function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

export function ensureHttps(url: string | null | undefined): string | null {
  if (!url) return null;
  let normalized = url.trim();
  if (!normalized) return null;
  if (!/^https?:\/\//i.test(normalized)) {
    normalized = `https://${normalized}`;
  }
  // Force www. on LinkedIn URLs to avoid bare-domain redirect that gets
  // blocked by COOP when navigating from inside an iframe (e.g. Lovable preview).
  try {
    const u = new URL(normalized);
    if (u.hostname === "linkedin.com") {
      u.hostname = "www.linkedin.com";
      normalized = u.toString();
    }
  } catch {
    // fall through with original normalized string
  }
  return normalized;
}

/**
 * Open an external URL in a new tab fully detached from the current window.
 * Using `noopener,noreferrer` strips `window.opener`, which prevents sites
 * with `Cross-Origin-Opener-Policy: same-origin` (like LinkedIn) from
 * blocking the navigation when launched from inside an iframe preview.
 */
export function openExternal(url: string | null | undefined): void {
  const normalized = ensureHttps(url);
  if (!normalized) return;
  // Route through a same-origin trampoline page so the destination opens
  // from a top-level context, not from inside the preview iframe. This
  // avoids COOP-based blocks (e.g. LinkedIn's ERR_BLOCKED_BY_RESPONSE).
  const redirectUrl = `/external-redirect.html?url=${encodeURIComponent(normalized)}`;
  window.open(redirectUrl, "_blank", "noopener,noreferrer");
}
